import type { PartialMessages } from './tr';
import { adminDe } from './admin.de';
import { mobileDe } from './mobile.de';

/** Almanca (F7-17): panel, vitrin ve mobil metinlerin tamamı; eksik anahtar raporu sıfır olmalı (`pnpm --filter @zt/i18n missing de`). */
import { commonDe, errorsDe } from './shared.de';
import { faz8PermissionsDe } from './faz8.de';
import { storefrontDe } from './storefront.de';
import { aiDe } from './ai.de';

export const de = {
  admin: adminDe,
  mobile: mobileDe,
  ai: aiDe.ui,
  common: commonDe,
  errors: errorsDe,
  roles: {
    owner: 'Geschäftsinhaber',
    admin: 'Administrator',
    store_manager: 'Shop-Manager',
    catalog_manager: 'Katalogverantwortlicher',
    pricing_manager: 'Preisverantwortlicher',
    operations: 'Bestellungen / Betrieb',
    warehouse: 'Lager',
    finance: 'Finanzen / Buchhaltung',
    marketing: 'Marketing',
    content_editor: 'Redakteur',
    vet_reviewer: 'Tierärztlicher Fachprüfer',
    customer_service: 'Kundenservice',
    dealer_rep: 'Außendienstmitarbeiter (Händler)',
    read_only: 'Nur Lesen / Prüfer',
  },
  roleDescriptions: {
    owner: 'Vollzugriff. Mindestens eine Person muss diese Rolle haben.',
    admin: 'Wie der Geschäftsinhaber, kann aber die Inhaberrolle nicht vergeben.',
    store_manager:
      'Leitet das Tagesgeschäft: Katalog, Bestellungen, Bestand, Kunden, Kampagnen. Keine Zahlungsschlüssel und keine Rollenverwaltung.',
    catalog_manager:
      'Verwaltet Produkte, Kategorien, Bilder und SEO-Felder. Sieht Preise, kann sie aber nicht in großen Mengen ändern.',
    pricing_manager: 'Verwaltet Preislisten, Massenpreisänderungen und Aktionspreise; sieht Kosten.',
    operations:
      'Bereitet Bestellungen vor, versendet sie und bearbeitet Rücksendeanfragen (ohne Genehmigung von Erstattungen).',
    warehouse:
      'Bestandsbewegungen, Inventuren und Umlagerungen; sieht keine Preise und keine Kontaktdaten von Kunden.',
    finance: 'Zahlungen, Erstattungen, Abgleich, Rechnungen und Finanzberichte.',
    marketing: 'Kampagnen, Gutscheincodes, E-Mail/SMS und Werbeberichte.',
    content_editor: 'Schreibt Entwürfe für Seiten und Artikel; kann nicht veröffentlichen.',
    vet_reviewer:
      'Genehmigt Gesundheits- und Nahrungsergänzungsinhalte sowie gesundheitsbezogene Aussagen zu Produkten. Gesundheitsbezogene Inhalte können ohne diese Genehmigung nicht veröffentlicht werden.',
    customer_service: 'Sieht Kunden und Bestellungen, beantwortet Support-Tickets.',
    dealer_rep: 'Verwaltet Bestellungen und Angebote zugewiesener Händler.',
    read_only: 'Sieht alles, ändert nichts; personenbezogene Daten sind maskiert.',
  },
  permissions: {
    ...aiDe.permissions,
    ...faz8PermissionsDe,
    'org.settings.read': 'Unternehmenseinstellungen anzeigen',
    'org.settings.update': 'Unternehmensdaten und -einstellungen ändern',
    'org.brand.read': 'Marken anzeigen',
    'org.brand.manage': 'Marken anlegen und bearbeiten',
    'org.store.read': 'Shops anzeigen',
    'org.store.manage': 'Shops, Domains und Märkte verwalten',
    'org.store.go_live': 'Shops live schalten, in Wartung versetzen oder offline nehmen (sensibel)',
    'org.data.migrate': 'Migrationspaket exportieren/importieren (nur Geschäftsinhaber; sensibel)',
    'org.user.read': 'Benutzer anzeigen',
    'org.user.manage': 'Benutzer einladen, Rollen ändern, sperren',
    'org.role.manage': 'Benutzerdefinierte Rollen anlegen und bearbeiten',
    'org.api_key.manage': 'API-Schlüssel erstellen und widerrufen',
    'org.webhook.manage': 'Webhooks verwalten',
    'org.integration.read': 'Integrationsstatus anzeigen',
    'org.integration.manage': 'Zugangsdaten für Integrationen eingeben und testen',
    'org.integration.go_live': 'Integrationen live schalten',
    'org.audit.read': 'Prüfprotokolle anzeigen',
    'org.system.read': 'Systemstatus anzeigen',
    'org.setup.manage': 'Einrichtungsassistenten verwalten',
    'org.help.manage': 'Hilfetexte bearbeiten',
    'org.file.upload': 'Dateien und Bilder hochladen',
    'org.job.read': 'Hintergrundaufgaben anzeigen',
    'catalog.product.read': 'Produkte anzeigen',
    'catalog.product.write': 'Produkte anlegen und bearbeiten',
    'catalog.product.publish': 'Produkte veröffentlichen',
    'catalog.health_claim.approve': 'Gesundheitsbezogene Aussagen zu Produkten genehmigen',
    'catalog.import': 'Produkte importieren',
    'catalog.bulk.apply': 'Massenvorgänge für Produkte anwenden und rückgängig machen',
    'pricing.price.read': 'Preise anzeigen',
    'pricing.price.write': 'Preise bearbeiten',
    'pricing.bulk.apply': 'Massenpreisänderungen anwenden',
    'pricing.cost.read': 'Kosten und Margen anzeigen',
    'inventory.stock.read': 'Bestand anzeigen',
    'inventory.stock.write': 'Bestandsbewegungen und Inventuren',
    'customers.customer.read': 'Kunden anzeigen',
    'customers.customer.write': 'Kunden bearbeiten',
    'customers.pii.read': 'Personenbezogene Kundendaten unmaskiert anzeigen',
    'privacy.request.read': 'Betroffenenanfragen anzeigen',
    'privacy.request.manage': 'Betroffenenanfragen erfassen und abschließen',
    'privacy.retention.read': 'Aufbewahrungsfristen und Löschprotokoll anzeigen',
    'privacy.retention.manage': 'Regeln für die automatische Löschung aktivieren und ändern',
    'privacy.data.export': 'Betroffene Person: Kopie der personenbezogenen Daten herunterladen',
    'privacy.data.erase': 'Betroffene Person: personenbezogene Daten anonymisieren (unumkehrbar)',
    'b2b.dealer.read': 'Händler anzeigen',
    'b2b.dealer.manage': 'Händlerdaten bearbeiten',
    'b2b.dealer.approve': 'Händleranträge genehmigen/ablehnen',
    'b2b.ledger.read': 'Händlerkonten anzeigen (Saldo, Kontoauszug)',
    'b2b.ledger.manage': 'Händlerzahlungen, Korrekturen und Anfangssalden erfassen; Zahlungsziele festlegen',
    'b2b.quote.read': 'Händlerangebote anzeigen',
    'b2b.quote.manage': 'Händlerangebote erstellen, senden und stornieren',
    'b2b.sales.manage': 'Vertriebsregionen verwalten; Händler Außendienstmitarbeitern und Regionen zuweisen',
    'b2b.commission.read': 'Provisionen der Außendienstmitarbeiter anzeigen',
    'b2b.commission.manage':
      'Provisionsregeln, Periodenberechnung, Genehmigung und Markierung als bezahlt verwalten',
    'orders.order.read': 'Bestellungen anzeigen',
    'orders.order.write': 'Bestellungen bearbeiten',
    'orders.refund.approve': 'Erstattungen genehmigen',
    'payments.payment.read': 'Zahlungen anzeigen',
    'payments.payment.capture':
      'Zahlungen per Banküberweisung, Nachnahme und offenem Konto als eingegangen markieren',
    'payments.giftcard.read': 'Geschenkkarten anzeigen',
    'payments.giftcard.issue': 'Geschenkkarten ausgeben, Guthaben anpassen, Karten deaktivieren (sensibel)',
    'customers.credit.manage': 'Shop-Guthaben von Kunden gutschreiben oder abziehen (sensibel)',
    'payments.provider.manage':
      'Zahlungseinstellungen, Bankkonten und Anbieterschlüssel; Kartenzahlungen live schalten',
    'shipping.shipment.write': 'Versandvorgänge',
    'shipping.method.manage': 'Versandarten und -tarife',
    'invoices.invoice.read': 'Rechnungen anzeigen',
    'invoices.invoice.write': 'Rechnungen erstellen und senden',
    'legal.document.read': 'Rechtstexte anzeigen',
    'legal.document.write': 'Rechtstexte entwerfen',
    'legal.document.publish': 'Rechtstexte veröffentlichen',
    'content.read': 'Inhalte anzeigen',
    'content.write': 'Inhalte schreiben',
    'content.publish': 'Inhalte veröffentlichen',
    'content.expert_review': 'Fachliche (tierärztliche) Freigabe erteilen',
    'catalog.question.read': 'Produktfragen anzeigen',
    'catalog.question.answer': 'Produktfragen beantworten und veröffentlichen',
    'catalog.review.read': 'Produktbewertungen anzeigen',
    'catalog.review.moderate': 'Produktbewertungen genehmigen, ablehnen und beantworten',
    'theme.read': 'Theme anzeigen',
    'theme.edit': 'Theme bearbeiten',
    'theme.publish': 'Theme veröffentlichen',
    'seo.read': 'SEO-Berichte anzeigen',
    'seo.write': 'SEO-Einstellungen bearbeiten',
    'tracking.read': 'Messeinstellungen und Statistiken zur Cookie-Einwilligung anzeigen',
    'tracking.manage': 'Storefront-Tags bearbeiten (GTM, GA4, Meta, TikTok)',
    'marketing.campaign.read': 'Kampagnen anzeigen',
    'marketing.audience.read': 'Abonnenten, Sperrliste und İYS-Einträge anzeigen',
    'marketing.audience.manage': 'Abonnements und Sperrliste verwalten, İYS-Einträge markieren',
    'marketing.segment.manage': 'Kundensegmente erstellen und RFM neu berechnen',
    'marketing.campaign.write': 'Kampagnen vorbereiten',
    'marketing.campaign.send': 'Kampagnen versenden',
    'reports.sales.read': 'Verkaufsberichte',
    'reports.finance.read': 'Finanzberichte',
    'automation.manage': 'Automatisierungen verwalten',
    'marketing.loyalty.manage': 'Treueprogramm verwalten',
    'marketing.referral.manage': 'Empfehlungsprogramm verwalten',
    'marketing.affiliate.manage': 'Affiliates und Provisionen verwalten',
    'customers.ticket.read': 'Support-Tickets anzeigen',
    'customers.ticket.manage': 'Support-Tickets beantworten und verwalten',
    'customers.chat.read': 'Live-Chat-Warteschlange anzeigen',
    'customers.chat.manage': 'Im Live-Chat antworten und Chat-Einstellungen verwalten',
    'marketing.ads.read': 'Werbezentrum anzeigen',
    'marketing.ads.manage': 'Werbekonten, Daten-Uploads und Änderungsanfragen verwalten',
    'marketing.ads.approve': 'Änderungen an Werbebudget und Veröffentlichung genehmigen',
  },
  permissionModules: {
    ...aiDe.permissionModules,
    org: 'Unternehmen & Einstellungen',
    catalog: 'Katalog',
    pricing: 'Preise',
    inventory: 'Bestand',
    customers: 'Kunden',
    b2b: 'Händler',
    orders: 'Bestellungen',
    payments: 'Zahlungen',
    shipping: 'Versand',
    invoices: 'Rechnungen',
    content: 'Inhalte',
    theme: 'Theme',
    seo: 'SEO',
    marketing: 'Marketing',
    reports: 'Berichte',
    automation: 'Automatisierung',
  },
  integrationStatus: {
    NOT_CONFIGURED: 'Einrichtung erforderlich',
    SANDBOX: 'Test (Sandbox)',
    LIVE: 'Live',
    ERROR: 'Fehler',
    DISABLED: 'Deaktiviert',
  },
  integrationCategories: {
    email: 'E-Mail',
    payment: 'Zahlungen',
    google: 'Google',
    ads: 'Werbung & Messung',
    messaging: 'Messaging & Einwilligungen',
    logistics: 'Versand',
    invoicing: 'e-Fatura / e-Arşiv',
    infrastructure: 'Infrastruktur',
    ai: 'KI',
    mobile: 'Mobil',
    seo: 'SEO-Daten',
  },
  integrations: {
    smtp: {
      name: 'SMTP-E-Mail',
      description: 'Versendet Bestell-, Passwort- und Einladungs-E-Mails über Ihren eigenen Mailserver.',
    },
    ses: {
      name: 'Amazon SES',
      description:
        'Versendet E-Mails über die Amazon SES API; Bounces und Beschwerden gehen über SNS ein und werden in die Sperrliste übernommen.',
    },
    sendgrid: {
      name: 'SendGrid',
      description:
        'Versendet E-Mails über die SendGrid API; signierter Event-Webhook für Bounces, Beschwerden und Abmeldungen.',
    },
    postmark: {
      name: 'Postmark',
      description:
        'Getrennte Postmark-Streams für Transaktions- und Newsletter-E-Mails; Webhooks für Bounces und Beschwerden.',
    },
    iyzico: {
      name: 'iyzico',
      description: 'Karten, 3D Secure, Ratenzahlung und Erstattungen.',
    },
    paytr: {
      name: 'PayTR',
      description: 'Karten, Ratenzahlung, Zahlungsseite für Banküberweisung.',
    },
    param: {
      name: 'Param',
      description:
        'Einrichtung erforderlich: Betragsformat, Rückgabefelder und Live-Adresse der gehosteten Zahlungsmethode von Param fehlen in der öffentlichen Dokumentation; Dokumentation bei Param anfordern.',
    },
    sipay: {
      name: 'Sipay',
      description:
        'Kreditkarte, 3D Secure und Ratenzahlung auf der Sipay-Zahlungsseite; Teilerstattungen. Für den Live-Betrieb ist eine Freigabe der statischen IP bei Sipay nötig.',
    },
    paynet: {
      name: 'Paynet',
      description:
        'Paynet wurde mit iyzico zusammengeführt (seit 1. Januar 2026 Marke iyzico); verwenden Sie die iyzico-Verbindung.',
    },
    stripe: {
      name: 'Stripe',
      description:
        'Kartenzahlungen in Fremdwährung und Wallets (Apple Pay, Google Pay) auf der Zahlungsseite von Stripe. Das Konto wird für ein Unternehmen in einem von Stripe unterstützten Land eröffnet (die Türkei wird nicht unterstützt).',
    },
    paypal: {
      name: 'PayPal',
      description:
        'Zahlung mit PayPal (EUR, USD, GBP). Das Konto gehört einem Unternehmen außerhalb der Türkei; PayPal ist in der Türkei nicht tätig.',
    },
    rank_tracking: {
      name: 'Ranking-Tracking',
      description:
        'Für die tägliche Verfolgung der Google-Rankings von Keywords ist ein lizenzierter (kostenpflichtiger) Datenanbieter nötig (z. B. DataForSEO, Semrush). Der Inhaber wählt den Anbieter; bis dahin stammt die Position aus dem Search-Console-Durchschnitt.',
    },
    pagespeed: {
      name: 'PageSpeed Insights',
      description:
        'Seitengeschwindigkeit (Core Web Vitals): Startseite, Kategorie, Produkt und Blogbeitrag werden wöchentlich gemessen. Google-Cloud-API-Schlüssel; kostenlos.',
    },
    google_oauth: {
      name: 'Google-Konto (OAuth-Client)',
      description:
        'Google-Cloud-OAuth-Client für Search Console, Analytics und Merchant Center. Die Verbindung erfolgt auf dem Google-Bildschirm.',
    },
    google_merchant: {
      name: 'Google Merchant Center',
      description: 'Produkt-Feed, kostenlose Einträge und Shopping.',
    },
    google_search_console: {
      name: 'Google Search Console',
      description: 'Suchleistung, Sitemaps und Indexierungsberichte.',
    },
    google_analytics: {
      name: 'Google Analytics 4',
      description: 'Analyse von Traffic und Verkäufen.',
    },
    google_tag_manager: {
      name: 'Google Tag Manager',
      description: 'Tag-Verwaltung.',
    },
    google_ads: {
      name: 'Google Ads',
      description: 'Werbeberichte und Conversion-Uploads.',
    },
    meta_ads: {
      name: 'Meta-Werbeausgaben',
      description:
        'Liest täglich Werbeausgaben, Impressionen und Klicks von Facebook und Instagram (nur Lesezugriff).',
    },
    tiktok_ads: {
      name: 'TikTok-Werbeausgaben',
      description: 'Liest täglich Werbeausgaben, Impressionen und Klicks von TikTok (nur Lesezugriff).',
    },
    google_business_profile: {
      name: 'Google Business Profile',
      description: 'Unternehmensinformationen und Bewertungen.',
    },
    meta: {
      name: 'Meta (Facebook/Instagram)',
      description: 'Pixel und Conversions API.',
    },
    tiktok: {
      name: 'TikTok',
      description: 'Pixel und Events API.',
    },
    iys: {
      name: 'İYS',
      description: 'Türkisches Register für Einwilligungen in kommerzielle Nachrichten (İYS).',
    },
    netgsm: {
      name: 'Netgsm (SMS)',
      description:
        'Kampagnen-SMS und Zustellberichte. Der Absendername muss bei Netgsm genehmigt und ein İYS-Markencode hinterlegt sein. Der Test fragt das Guthaben ab; es wird keine SMS gesendet.',
    },
    iletimerkezi: {
      name: 'İleti Merkezi (SMS)',
      description:
        'Kampagnen-SMS und Zustellberichte. Der Absendername muss genehmigt sein. Der Test fragt das Guthaben ab; es wird keine SMS gesendet.',
    },
    verimor: {
      name: 'Verimor (SMS)',
      description:
        'Kampagnen-SMS und Zustellberichte. Verimor erfordert eine API-IP-Freigabeliste und einen genehmigten Absendernamen. Der Test fragt das Guthaben ab; es wird keine SMS gesendet.',
    },
    whatsapp: {
      name: 'WhatsApp Business (Cloud API)',
      description:
        'Kampagnennachrichten mit von Meta genehmigten Vorlagen und Zustell-Webhooks. Der Test liest die Telefonnummerndaten; es wird keine Nachricht gesendet.',
    },
    yurtici: {
      name: 'Yurtiçi Kargo',
      description: 'Sendungsregistrierung und Sendungsverfolgung (Webservice des Versanddienstleisters).',
    },
    aras: {
      name: 'Aras Kargo',
      description: 'Sendungsregistrierung und Sendungsverfolgung (Webservice des Versanddienstleisters).',
    },
    mng: {
      name: 'MNG Kargo (DHL eCommerce)',
      description: 'Sendungen, Barcode-Etiketten und Sendungsverfolgung (Apizone).',
    },
    geliver: {
      name: 'Geliver (mehrere Versanddienstleister)',
      description: 'Viele Versanddienstleister über eine Verbindung: Tarife, Etiketten, Sendungsverfolgung.',
    },
    google_signin: {
      name: 'Mit Google anmelden (Kunden)',
      description:
        '„Weiter mit Google“ für Shop-Kunden: eigener OAuth-Client; nur die Bereiche openid, E-Mail, Profil. Pro Shop unter Shops → Kundenanmeldung aktivieren.',
    },
    bizimhesap: {
      name: 'BizimHesap',
      description: 'Rechnungsdatensätze aus Bestellungen; e-Arşiv/e-Fatura wird in BizimHesap ausgestellt.',
    },
    cloudflare: {
      name: 'Cloudflare',
      description:
        'CDN, WAF und DNS. Ob es genutzt wird, entscheidet der Inhaber (optional); die Website funktioniert ohne Cloudflare.',
    },
    s3_storage: {
      name: 'Objektspeicher (S3 / R2)',
      description:
        'Cloudflare R2, Backblaze B2, Hetzner oder ein anderer S3-kompatibler Speicher: Bilder und Dokumente außerhalb des Servers sowie eine verschlüsselte externe Kopie der nächtlichen Backups.',
    },
    error_tracking: {
      name: 'Fehlerüberwachung',
      description: 'Anwendungsfehler überwachen.',
    },
    observability: {
      name: 'Beobachtbarkeit (OpenTelemetry)',
      description:
        'Anfrage- und Hintergrundauftragsdauern, Fehlerraten und Traces werden per OTLP an einen Collector gesendet (Grafana Cloud, Honeycomb, New Relic, eigener OpenTelemetry Collector). Keine personenbezogenen Daten: nur Methode, Routenvorlage, Statuscode und Auftragsname.',
    },
    firebase: {
      name: 'Firebase (Android-Benachrichtigungen)',
      description:
        'Benachrichtigungen wie neue Bestellungen und Rücksendeanfragen in der Android-Verwaltungs-App (FCM). Geben Sie project_id, client_email und private_key aus dem Firebase-Dienstkontoschlüssel ein.',
    },
    // Faz 9: ai_text, ai_image ve youtube metinleri ai.*.ts'de.
    ...aiDe.integrations,
  },
  integrationFields: {
    ...aiDe.integrationFields,
    iysCode: 'İYS-Nummer (Dienstanbietercode)',
    brandCode: 'Markencode',
    autoSend: 'Einwilligungen und Ablehnungen automatisch an İYS senden',
    endpoint: 'Endpunkt-URL (S3 API, https://…)',
    region: 'Region (z. B. eu-central-1 für Amazon; auto für R2, leer = auto)',
    bucket: 'Bucket-Name',
    accessKeyId: 'Access Key ID',
    secretAccessKey: 'Secret Access Key',
    prefix: 'Ordnerpräfix (optional, z. B. zootopia/)',
    storeMedia: 'Neue Bilder und Dokumente in diesem Speicher ablegen',
    backupCopies: 'Verschlüsselte externe Kopie der Backups hierher hochladen',
    backupKeepDays: 'Aufbewahrung externer Backups (Tage; leer = 30)',
    dsn: 'DSN (Projektadresse der Fehlerüberwachung)',
    otlpEndpoint: 'Collector-Adresse (OTLP/HTTP, https://…)',
    otlpHeaders: 'Auth-Header (Name=Wert, durch Kommas getrennt; z. B. Authorization=Basic …)',
    sampleRatio: 'Trace-Stichprobenrate (0–1; leer = 1, alle Anfragen)',
    host: 'Server-Host',
    port: 'Port',
    secure: 'SSL/TLS (für Port 465 aktivieren)',
    username: 'Benutzername',
    password: 'Passwort',
    fromEmail: 'Absender-E-Mail',
    fromName: 'Absendername',
    apiKey: 'API-Schlüssel',
    secretKey: 'Geheimer Schlüssel',
    merchantId: 'Händler-ID',
    merchantKey: 'Händlerschlüssel',
    merchantSalt: 'Händler-Salt',
    wsUserName: 'Webservice-Benutzername',
    wsPassword: 'Webservice-Passwort',
    codUserName: 'Benutzername für Nachnahme (falls vorhanden)',
    codPassword: 'Passwort für Nachnahme (falls vorhanden)',
    setOrderUserName: 'Benutzername für Sendungserstellung',
    setOrderPassword: 'Passwort für Sendungserstellung',
    queryUserName: 'Benutzername für Abfragen',
    queryPassword: 'Passwort für Abfragen',
    customerCode: 'Kundencode',
    clientId: 'Client-ID',
    clientSecret: 'Client-Secret',
    webhookSecret: 'Webhook-Signaturschlüssel (whsec_…)',
    appId: 'Anwendungs-ID (app_id)',
    saleWebhookKey: 'Webhook-Schlüssel für Verkäufe',
    webhookId: 'Webhook-ID',
    customerNumber: 'Kundennummer',
    apiToken: 'API-Token',
    firmId: 'Firmen-ID (firmId)',
    token: 'Zugriffstoken (bei leerem Feld wird die Firmen-ID verwendet)',
    configurationSetName: 'Configuration Set (optional)',
    snsTopicArn: 'SNS Topic ARN (für Bounce-/Beschwerdebenachrichtigungen)',
    webhookPublicKey: 'Öffentlicher Schlüssel des Signed Event Webhook',
    euRegion: 'Konto in der EU-Region (api.eu.sendgrid.com)',
    serverToken: 'Server-API-Token',
    transactionalStream: 'Transaktions-Stream (Standard: outbound)',
    broadcastStream: 'Broadcast-Stream-ID (Standard: broadcast)',
    apiHash: 'API-Secret (Hash)',
    header: 'SMS-Absendername (genehmigter Absender, max. 11 Zeichen)',
    accessToken: 'Zugriffstoken (Systembenutzer, dauerhaft)',
    phoneNumberId: 'Telefonnummer-ID',
    wabaId: 'WhatsApp-Business-Konto-ID (WABA ID)',
    appSecret: 'Anwendungsschlüssel (app_secret / App-Secret)',
    verifyToken: 'Webhook-Verifizierungstoken (von Ihnen festgelegt)',
    apiVersion: 'Graph-API-Version (optional, Standard: v23.0)',
    projectId: 'Projekt-ID (project_id)',
    clientEmail: 'E-Mail des Dienstkontos (client_email)',
    privateKey: 'Privater Schlüssel (der vollständige private_key-Wert aus der JSON-Datei)',
    loginCustomerId: 'Verwaltungskonto-ID (MCC, optional, 123-456-7890)',
    adAccountId: 'Werbekonto-ID (mit act_ oder nur Ziffern)',
    advertiserId: 'Werbetreibenden-ID',
  },
  integrationMessages: {
    ...aiDe.integrationMessages,
    health_timeout: 'Der Anbieter hat nicht rechtzeitig geantwortet.',
    health_unreachable: 'Der Anbieter ist nicht erreichbar.',
    health_skipped_messages:
      'Dieser Test sendet eine echte Nachricht oder ein Ereignis an den Anbieter und wird daher nicht automatisch ausgeführt. Nutzen Sie „Verbindung testen“ der Verbindung.',
    health_skipped_remote_write:
      'Dieser Test legt beim Anbieter einen Testeintrag an und löscht ihn wieder; er wird nicht automatisch ausgeführt. Nutzen Sie „Verbindung testen“ der Verbindung.',
    health_skipped_paid_quota:
      'Dieser Test verbraucht kostenpflichtiges Kontingent und wird nicht automatisch ausgeführt. Nutzen Sie „Verbindung testen“ der Verbindung.',
    health_skipped_unknown: 'Für diese Verbindung ist keine automatische Prüfung festgelegt.',
    ses_ok: 'Mit Amazon SES verbunden; der Versand ist aktiviert.',
    ses_ok_sandbox:
      'Mit Amazon SES verbunden, aber das Konto befindet sich in der Sandbox und sendet nur an verifizierte Adressen. Beantragen Sie bei AWS den Produktionszugriff.',
    ses_invalid_settings:
      'Region (z. B. eu-central-1), Zugriffsschlüssel und Absender-E-Mail sind erforderlich.',
    ses_auth_failed:
      'Die Amazon-Zugriffsschlüssel wurden nicht akzeptiert oder haben keine SES-Berechtigung.',
    ses_unreachable: 'Amazon SES ist nicht erreichbar.',
    ses_sending_disabled:
      'Der Versand ist für dieses Amazon-SES-Konto deaktiviert (möglicherweise pausiert).',
    sendgrid_ok: 'Mit SendGrid verbunden; der Schlüssel darf E-Mails versenden.',
    sendgrid_invalid_settings: 'API-Schlüssel und Absender-E-Mail sind erforderlich.',
    sendgrid_auth_failed: 'Der SendGrid-API-Schlüssel wurde nicht akzeptiert.',
    sendgrid_missing_scope: 'Dem API-Schlüssel fehlt die Berechtigung "Mail Send".',
    sendgrid_unreachable: 'SendGrid ist nicht erreichbar.',
    postmark_ok: 'Mit Postmark verbunden.',
    postmark_invalid_settings: 'Server-Token und Absender-E-Mail sind erforderlich.',
    postmark_auth_failed: 'Das Postmark-Server-Token wurde nicht akzeptiert.',
    postmark_unreachable: 'Postmark ist nicht erreichbar.',
    iys_ok: 'Mit İYS verbunden (Token erhalten); es wurde keine Einwilligung gesendet.',
    iys_invalid_settings:
      'İYS-Nummer, Markencode (nur Ziffern), Benutzername und Passwort sind erforderlich.',
    iys_auth_failed: 'Der İYS-Benutzername oder das Passwort wurde nicht akzeptiert.',
    iys_rejected: 'İYS hat den Eintrag abgelehnt.',
    iys_unreachable: 'İYS ist nicht erreichbar.',
    s3_ok: 'Eine Testdatei wurde geschrieben, zurückgelesen und gelöscht; die Verbindung funktioniert.',
    s3_endpoint_invalid:
      'Endpunkt, Bucket-Name oder Schlüssel fehlen oder sind ungültig (eine https-S3-API-URL ist erforderlich).',
    s3_auth_failed:
      'Die Speicherschlüssel wurden abgelehnt oder haben keinen Schreibzugriff auf diesen Bucket.',
    s3_bucket_missing:
      'Es gibt keinen Bucket mit diesem Namen; erstellen Sie ihn in der Konsole des Anbieters.',
    s3_unreachable: 'Der Speicherserver ist nicht erreichbar.',
    s3_verify_failed: 'Die Testdatei konnte nicht unverändert zurückgelesen werden.',
    error_tracking_ok:
      'Ein Testereignis wurde an den Fehlerüberwachungsdienst gesendet; "Zootopia bağlantı testi" sollte in dessen Dashboard erscheinen.',
    error_tracking_invalid_dsn:
      'Ungültiger DSN. Fügen Sie die Adresse unter "Client Keys (DSN)" aus den Projekteinstellungen ein.',
    error_tracking_failed:
      'Der Fehlerüberwachungsdienst hat das Testereignis nicht angenommen; prüfen Sie DSN und Projekt.',
    observability_ok:
      'Der Collector hat ein leeres Trace-Paket angenommen. Anfrage- und Auftragsdauern erscheinen in wenigen Minuten im Collector.',
    observability_invalid:
      'Collector-Adresse oder Header ungültig. Die Adresse muss mit https beginnen (z. B. https://otlp-gateway-….grafana.net/otlp); Header als „Name=Wert“, durch Kommas getrennt.',
    observability_failed:
      'Der Collector hat das Testpaket nicht angenommen; prüfen Sie Adresse und Auth-Header (z. B. Authorization) in den OTLP-Einstellungen des Collectors.',
    pagespeed_ok: 'PageSpeed-Insights-Schlüssel verifiziert; eine Beispielseite wurde gemessen.',
    pagespeed_key_format: 'Das Format des API-Schlüssels ist falsch.',
    pagespeed_key_invalid: 'Google hat diesen API-Schlüssel nicht akzeptiert.',
    pagespeed_quota: 'Das Tageskontingent von PageSpeed Insights ist aufgebraucht.',
    pagespeed_unreachable: 'PageSpeed Insights ist nicht erreichbar.',
    smtp_ok: 'Mit dem SMTP-Server verbunden und authentifiziert.',
    google_client_ok:
      'Google-Client-ID und Secret verifiziert. Verbinden Sie jetzt Ihr Konto auf dem Google-Bildschirm.',
    google_client_invalid: 'Google hat die Client-ID oder das Secret nicht akzeptiert.',
    google_client_invalid_format:
      'Die Client-ID muss auf ".apps.googleusercontent.com" enden; das Secret darf nicht leer sein.',
    google_signin_needs_domain:
      'Einrichtung erforderlich: Der Test verwendet die echte Weiterleitungs-URI des Shops. Fügen Sie zuerst unter Shops eine Shop-Domain hinzu.',
    google_invalid_request:
      'Google hat die Anfrage abgelehnt (invalid_request): {{description}} Versuchte Weiterleitungs-URI: {{redirectUri}}. Sie muss eine echte https-Domain sein und in Google Cloud zu den autorisierten Weiterleitungs-URIs des Clients gehören.',
    google_redirect_uri_mismatch:
      'Die Weiterleitungs-URI ist beim Client in Google Cloud nicht registriert (redirect_uri_mismatch): {{redirectUri}}. Fügen Sie sie unter Google Auth Platform → Clients → Client → Autorisierte Weiterleitungs-URIs hinzu. {{description}}',
    google_client_rejected: 'Google hat die Anfrage abgelehnt ({{error}}): {{description}}',
    google_unreachable: 'Google ist nicht erreichbar. Versuchen Sie es später erneut.',
    smtp_failed: 'SMTP-Verbindung fehlgeschlagen.',
    smtp_invalid_settings: 'Host oder Port ist ungültig.',
    smtp_private_host_blocked: 'Interne Netzwerkadressen sind aus Sicherheitsgründen gesperrt.',
    iyzico_ok: 'iyzico-Verbindung verifiziert.',
    iyzico_credentials_missing: 'iyzico-API-Schlüssel und geheimer Schlüssel fehlen.',
    iyzico_not_sandbox_key:
      'Die Testumgebung benötigt einen iyzico-Testschlüssel, der mit "sandbox-" beginnt.',
    iyzico_sandbox_key_in_live: 'Für die Live-Umgebung wurde ein Testschlüssel (Sandbox) eingegeben.',
    iyzico_auth_failed: 'iyzico hat die Schlüssel abgelehnt.',
    iyzico_unreachable: 'iyzico ist nicht erreichbar. Versuchen Sie es später erneut.',
    paytr_ok: 'PayTR-Verbindung verifiziert.',
    paytr_credentials_missing: 'PayTR-Händler-ID, Händlerschlüssel und Salt fehlen.',
    paytr_auth_failed: 'PayTR hat die Zugangsdaten abgelehnt.',
    paytr_unreachable: 'PayTR ist nicht erreichbar. Versuchen Sie es später erneut.',
    stripe_ok: 'Stripe-Verbindung verifiziert (Saldo gelesen).',
    stripe_credentials_missing: 'Stripe-Geheimschlüssel und Webhook-Signaturschlüssel fehlen.',
    stripe_key_invalid:
      'Der geheime Schlüssel muss mit "sk_" (oder eingeschränkt "rk_") beginnen, nicht der veröffentlichbare Schlüssel "pk_".',
    stripe_not_test_key: 'Die Testumgebung benötigt einen Testschlüssel, der mit "sk_test_" beginnt.',
    stripe_test_key_in_live:
      'Für die Live-Umgebung wurde ein Testschlüssel eingegeben ("sk_live_" erforderlich).',
    stripe_webhook_secret_invalid:
      'Der Webhook-Signaturschlüssel muss mit "whsec_" beginnen (Stripe → Webhooks → Endpunkt).',
    stripe_mode_mismatch: 'Der Modus des Schlüssels (Test/Live) passt nicht zur gewählten Umgebung.',
    stripe_auth_failed: 'Stripe hat den Schlüssel nicht akzeptiert.',
    stripe_unreachable: 'Stripe ist nicht erreichbar. Versuchen Sie es später erneut.',
    sipay_ok: 'Sipay-Verbindung bestätigt (Zugriffstoken erhalten).',
    sipay_credentials_missing: 'Sipay-Anwendungs-ID, Anwendungsschlüssel und Händlerschlüssel fehlen.',
    sipay_auth_failed: 'Die Sipay-Zugangsdaten wurden nicht akzeptiert.',
    sipay_unreachable: 'Sipay ist nicht erreichbar. Versuchen Sie es später erneut.',
    google_signin_client_ok: 'Google hat Client-ID und Secret akzeptiert.',
    geliver_ok:
      'Geliver-Verbindung bestätigt; {{senders}} Absenderadresse(n) gefunden. Live: Etikettgebühren werden vom Geliver-Guthaben abgebucht.',
    geliver_ok_test:
      'Geliver-Verbindung bestätigt; {{senders}} Absenderadresse(n) gefunden. Testumgebung: Sendungen werden in Geliver als Testsendungen angelegt.',
    geliver_credentials_missing: 'Das Geliver-API-Token fehlt.',
    geliver_auth_failed:
      'Geliver hat das API-Token abgelehnt. Prüfen Sie das Token im Geliver-Panel (API-Tokens).',
    geliver_timeout:
      'Geliver hat nicht innerhalb von 30 Sekunden geantwortet. Versuchen Sie es später erneut.',
    geliver_unreachable: 'Geliver ist nicht erreichbar. Versuchen Sie es später erneut.',
    geliver_unavailable:
      'Geliver kann gerade nicht antworten (HTTP {{status}}). Versuchen Sie es in einigen Minuten erneut; das Etikett wird nicht doppelt gekauft.',
    geliver_rejected: 'Geliver hat die Anfrage abgelehnt: {{reason}}',
    geliver_unexpected_response:
      'Geliver hat eine unerwartete Antwort gesendet; der Vorgang wurde nicht abgeschlossen.',
    paypal_ok: 'PayPal-Verbindung verifiziert (Webhook gefunden).',
    paypal_credentials_missing: 'PayPal-Client-ID, Secret und Webhook-ID fehlen.',
    paypal_auth_failed: 'PayPal hat die Client-ID oder das Secret nicht akzeptiert.',
    paypal_webhook_not_found: 'Diese Webhook-ID wurde in Ihrer PayPal-App nicht gefunden.',
    paypal_unreachable: 'PayPal ist nicht erreichbar. Versuchen Sie es später erneut.',
    bizimhesap_ok: 'BizimHesap-Verbindung verifiziert.',
    bizimhesap_credentials_missing: 'BizimHesap-Firmen-ID (firmId) fehlt.',
    bizimhesap_auth_failed: 'BizimHesap hat die Zugangsdaten abgelehnt. Prüfen Sie Firmen-ID/Schlüssel.',
    bizimhesap_unreachable: 'BizimHesap ist nicht erreichbar. Versuchen Sie es später erneut.',
    bizimhesap_redirected:
      'BizimHesap hat die Anfrage umgeleitet (HTTP {{status}} → {{location}}). Die BizimHesap-API-Adresse hat sich möglicherweise geändert; die Plattform muss aktualisiert werden.',
    bizimhesap_unexpected_response:
      'BizimHesap hat eine unerwartete Antwort geliefert (HTTP {{status}}, {{contentType}}); sie ist kein JSON. Der Dienst wird möglicherweise gewartet oder die API-Adresse hat sich geändert.',
    bizimhesap_timeout:
      'BizimHesap hat nicht innerhalb von 20 Sekunden geantwortet. Versuchen Sie es später erneut.',
    netgsm_ok: 'Netgsm-Verbindung verifiziert (Guthaben gelesen); keine SMS gesendet.',
    netgsm_invalid_settings:
      'Zugangsdaten und ein SMS-Absendername mit höchstens 11 Zeichen sind erforderlich.',
    netgsm_auth_failed: 'Netgsm hat die Zugangsdaten abgelehnt.',
    netgsm_unreachable: 'Netgsm ist nicht erreichbar. Versuchen Sie es später erneut.',
    netgsm_rejected: 'Netgsm hat die Anfrage abgelehnt; prüfen Sie die Kontoeinstellungen.',
    iletimerkezi_ok: 'İleti-Merkezi-Verbindung verifiziert (Guthaben gelesen); keine SMS gesendet.',
    iletimerkezi_invalid_settings:
      'Zugangsdaten und ein SMS-Absendername mit höchstens 11 Zeichen sind erforderlich.',
    iletimerkezi_auth_failed: 'İleti Merkezi hat die Zugangsdaten abgelehnt.',
    iletimerkezi_unreachable: 'İleti Merkezi ist nicht erreichbar. Versuchen Sie es später erneut.',
    iletimerkezi_rejected: 'İleti Merkezi hat die Anfrage abgelehnt; prüfen Sie die Kontoeinstellungen.',
    verimor_ok: 'Verimor-Verbindung verifiziert (Guthaben gelesen); keine SMS gesendet.',
    verimor_invalid_settings:
      'Zugangsdaten und ein SMS-Absendername mit höchstens 11 Zeichen sind erforderlich.',
    verimor_auth_failed: 'Verimor hat die Zugangsdaten abgelehnt.',
    verimor_unreachable: 'Verimor ist nicht erreichbar. Prüfen Sie die IP-Freigabeliste.',
    verimor_rejected: 'Verimor hat die Anfrage abgelehnt; prüfen Sie die Kontoeinstellungen.',
    whatsapp_ok: 'WhatsApp-Business-Verbindung verifiziert (Nummerndaten gelesen); keine Nachricht gesendet.',
    whatsapp_invalid_settings:
      'Zugriffstoken, Telefonnummer-ID, WABA ID, App-Secret und Verifizierungstoken sind erforderlich.',
    whatsapp_auth_failed: 'Meta hat das Zugriffstoken abgelehnt oder ihm fehlt die Berechtigung.',
    whatsapp_unreachable: 'Meta (WhatsApp) ist nicht erreichbar.',
    whatsapp_rejected: 'Meta hat die Anfrage abgelehnt; prüfen Sie die Telefonnummer-ID.',
    firebase_ok:
      'Firebase-Dienstkonto verifiziert (Zugriffstoken erhalten); keine Benachrichtigung gesendet.',
    firebase_invalid_settings:
      'Projekt-ID, eine Dienstkonto-E-Mail mit der Endung …iam.gserviceaccount.com und der private Schlüssel (PEM) sind erforderlich.',
    firebase_auth_failed:
      'Google hat das Dienstkonto abgelehnt; prüfen Sie, ob der Schlüssel nicht gelöscht wurde und die E-Mail korrekt ist.',
    firebase_unreachable: 'Google ist nicht erreichbar. Versuchen Sie es später erneut.',
    google_ads_invalid_settings: 'Die Verwaltungskonto-ID (MCC) muss 10 Ziffern haben (z. B. 123-456-7890).',
    google_ads_settings_saved:
      'Gespeichert. Ein Entwickler-Token ist nicht mehr nötig (Google, 9. September 2026); der Zugriff hängt von der Stufe Ihres Google-Cloud-Projekts ab. Die Verwaltungskonto-ID (MCC) ist nur nötig, wenn Sie über ein Verwaltungskonto zugreifen.',
    google_ads_cloud_project_not_approved:
      'Ihr Google-Cloud-Projekt kann mit der Google Ads API nur auf Testkonten zugreifen (Testzugriff). Beantragen Sie für ein echtes Werbekonto in Google Cloud den Explorer-Zugriff: Google Cloud Console → Google Ads API → Übersicht (das Projekt des OAuth-Clients der Google-Verbindung).',
    google_ads_user_permission_denied:
      'Das verbundene Google-Konto hat keinen Zugriff auf dieses Google-Ads-Konto. Wenn Sie über ein Verwaltungskonto (MCC) zugreifen, geben Sie die MCC-ID unter Integrationen → Google Ads ein; falls bereits geschehen, prüfen Sie, ob es dieses Konto verwaltet.',
    google_ads_customer_not_enabled:
      'Dieses Google-Ads-Konto ist noch nicht aktiviert oder wurde geschlossen.',
    google_ads_authorization_error:
      'Google Ads hat die Autorisierung abgelehnt. Wurde die Zugriffsstufe des Cloud-Projekts nach dem 9. September 2026 erhöht, kann dies ein von Google gemeldetes bekanntes Problem sein: Erstellen Sie ein neues Google-Cloud-Projekt und beantragen Sie den Explorer-Zugriff.',
    google_ads_api_not_enabled:
      'Die Google Ads API ist im Google-Cloud-Projekt nicht aktiviert oder die Nutzungsbedingungen wurden nicht akzeptiert. Aktivieren Sie die Google Ads API in der Google Cloud Console.',
    google_scope_missing:
      'Für diesen Dienst wurde keine Google-Berechtigung erteilt. Verbinden Sie sich erneut und wählen Sie dabei den Dienst aus.',
    google_permission_denied:
      'Das verbundene Google-Konto hat keinen Zugriff auf diese Ressource. Prüfen Sie, ob die Ressource (Property, Website, Konto) für dieses Google-Konto freigegeben ist, oder verbinden Sie ein Konto mit Zugriff.',
    google_api_not_enabled:
      'Die API dieses Google-Dienstes ist im Google-Cloud-Projekt nicht aktiviert. Aktivieren Sie sie in der Google Cloud Console → APIs & Dienste → Bibliothek (das Projekt des OAuth-Clients der Google-Verbindung).',
    google_quota_exceeded:
      'Google-Anfragelimit erreicht. Versuchen Sie es in Kürze erneut; Synchronisierungen werden automatisch wiederholt.',
    google_ads_quota_exceeded:
      'Anfragelimit der Google Ads API erreicht. Beim Explorer-Zugriff gilt für echte Konten ein tägliches Vorgangslimit; der Vorgang wird später automatisch wiederholt.',
    google_ads_access_prohibited:
      'Der Zugriff auf die Google Ads API ist für dieses Google-Cloud-Projekt gesperrt. Prüfen Sie die Zugriffsstufe der Google Ads API in der Google Cloud Console.',
    google_ads_action_not_permitted:
      'Das verbundene Google-Konto darf diesen Vorgang in diesem Google-Ads-Konto nicht ausführen (eventuell nur Lesezugriff). Verbinden Sie ein Konto mit Standard- oder Administratorzugriff.',
    google_ads_account_suspended:
      'Das Google-Ads-Konto ist gesperrt; Änderungen sind nicht möglich. Prüfen Sie den Kontostatus in Google Ads.',
    google_ads_not_allowlisted:
      'Diese Google-Ads-Funktion ist für Ihr Konto nicht verfügbar (Freischaltung durch Google erforderlich).',
    google_ads_policy_violation:
      'Aufgrund der Google-Werberichtlinien abgelehnt. Bearbeiten Sie Text oder Produkt; bei Gesundheitsprodukten sind Aussagen wie „heilt“, „verhindert“ oder „behandelt“ nicht erlaubt. Details im Richtlinienstatus der Kampagne.',
    google_ads_budget_too_low:
      'Das Tagesbudget ist für Google Ads zu niedrig. Geben Sie ein höheres Tagesbudget ein.',
    google_ads_budget_too_high: 'Das Tagesbudget überschreitet das Google-Ads-Maximum.',
    google_ads_budget_currency: 'Der Budgetbetrag passt nicht zur Kontowährung oder deren kleinster Einheit.',
    google_ads_budget_invalid:
      'Google Ads hat die Budgeteinstellung abgelehnt. Prüfen Sie, ob das Budget nicht von einer anderen Kampagne verwendet wird und nicht entfernt wurde.',
    google_ads_duplicate_name:
      'Eine Kampagne oder ein Budget mit diesem Namen existiert bereits. Geben Sie einen anderen Namen ein.',
    google_ads_bid_out_of_range: 'Das Gebot liegt außerhalb der Google-Ads-Grenzen.',
    google_ads_bidding_invalid:
      'Die gewählte Gebotsstrategie kann mit diesem Kampagnentyp oder Budget nicht verwendet werden.',
    google_ads_temporary_error:
      'Google Ads hat einen vorübergehenden Fehler gemeldet. Der Vorgang wird in Kürze automatisch wiederholt.',
    google_ads_resource_not_found:
      'Die Ressource wurde in Google Ads nicht gefunden (eventuell in Google Ads entfernt). Synchronisieren Sie und versuchen Sie es erneut.',
    google_ads_value_out_of_range: 'Der Wert liegt außerhalb der Google-Ads-Grenzen.',
    google_ads_resource_limit:
      'Das Google-Ads-Konto hat ein Ressourcenlimit erreicht (z. B. Anzahl der Kampagnen oder Assets).',
    google_ads_request_rejected:
      'Google Ads hat die Anfrage abgelehnt. Details im Google-Verbindungsprotokoll.',
    ads_ok:
      'Werbekonto gelesen: {{account}} ({{currency}}). Die Ausgabendaten werden jeden Morgen synchronisiert.',
    ads_credentials_format:
      'Zugriffstoken und Konto-ID sind erforderlich; die Konto-ID darf nur Ziffern enthalten.',
    ads_token_invalid:
      'Das Zugriffstoken ist ungültig oder abgelaufen. Erstellen Sie ein neues Token und speichern Sie es.',
    ads_permission: 'Das Token kann dieses Werbekonto nicht lesen (Berechtigung ads_read / Berichte).',
    ads_rate_limited:
      'Anfragelimit der Plattform erreicht; die Synchronisierung wird später erneut versucht.',
    ads_account_not_found: 'Werbekonto nicht gefunden; prüfen Sie die ID.',
    ads_unreachable: 'Die Werbeplattform ist nicht erreichbar. Versuchen Sie es später erneut.',
    ads_timeout: 'Die Werbeplattform hat nicht rechtzeitig geantwortet. Versuchen Sie es später erneut.',
    ads_bad_response: 'Unerwartete Antwort der Werbeplattform.',
    ads_provider_error:
      'Die Werbeplattform hat die Anfrage abgelehnt. Details finden Sie im Fehlerprotokoll der Plattform.',
  },
  setupSteps: {
    business: 'Unternehmensdaten',
    brand: 'Marke hinzufügen',
    logo: 'Logo hochladen',
    colors: 'Farben wählen',
    domain: 'Domain verbinden',
    currency: 'Währung und Markt',
    tax: 'Steuern einrichten',
    import_products: 'Produkte importieren',
    warehouse: 'Lager anlegen',
    shipping: 'Versand verbinden',
    pos_application: 'POS-Antrag vorbereiten',
    pos_connect: 'POS verbinden',
    email_service: 'E-Mail-Dienst verbinden',
    search_console: 'Search Console verbinden',
    ga4: 'GA4 verbinden',
    gtm: 'GTM verbinden',
    merchant_center: 'Merchant Center verbinden',
    seo: 'SEO-Einstellungen abschließen',
    legal_pages: 'Rechtliche Seiten prüfen',
    test_order: 'Testbestellung aufgeben',
    payment_tests: 'Zahlungstests durchführen',
    security_checks: 'Sicherheitsprüfungen abschließen',
    preview_store: 'Shop-Vorschau anzeigen',
    go_live: 'Mit Freigabe live schalten',
  },
  setupStatus: {
    done: 'Erledigt',
    pending: 'Ausstehend',
    skipped: 'Übersprungen',
    locked: 'Gesperrt',
  },
  storefront: storefrontDe,
  auditActions: {
    ...aiDe.auditActions,
    'company.member.add': 'Firmenbenutzer hinzugefügt',
    'company.member.update': 'Rolle oder Schwelle des Firmenbenutzers geändert',
    'company.member.remove': 'Firmenbenutzer entfernt',
    'customer.access_link.activation': 'Aktivierungslink erstellt',
    'api_key.create': 'API-Schlüssel erstellt',
    'api_key.revoke': 'API-Schlüssel widerrufen',
    'brand.create': 'Marke hinzugefügt',
    'brand.update': 'Marke aktualisiert',
    'domain.create': 'Domain hinzugefügt',
    'domain.delete': 'Domain entfernt',
    'help.update': 'Hilfetext aktualisiert',
    'integration.credentials_saved': 'Zugangsdaten der Integration gespeichert',
    'integration.disconnected': 'Integration getrennt',
    'integration.tested': 'Integration getestet',
    'market.create': 'Markt hinzugefügt',
    'market.delete': 'Markt entfernt',
    'member.invitation_accepted': 'Einladung angenommen',
    'member.invitation_revoke': 'Einladung zurückgezogen',
    'member.invite': 'Benutzer eingeladen',
    'member.remove': 'Benutzer entfernt',
    'member.update': 'Benutzerrolle geändert',
    'organization.update': 'Unternehmensdaten aktualisiert',
    'role.create': 'Benutzerdefinierte Rolle erstellt',
    'role.delete': 'Benutzerdefinierte Rolle gelöscht',
    'role.update': 'Benutzerdefinierte Rolle aktualisiert',
    'settings.update': 'Einstellungen aktualisiert',
    'setup.step_marked': 'Einrichtungsschritt markiert',
    'store.create': 'Shop erstellt',
    'store.update': 'Shop aktualisiert',
    'webhook.create': 'Webhook hinzugefügt',
    'webhook.delete': 'Webhook gelöscht',
    'webhook.redeliver': 'Webhook erneut gesendet',
    'webhook.rotate_secret': 'Webhook-Signaturgeheimnis erneuert',
    'webhook.update': 'Webhook aktualisiert',
  },
  auditEntities: {
    company: 'Händlerfirma',
    customer: 'Kunde',
    api_key: 'API-Schlüssel',
    brand: 'Marke',
    custom_role: 'Benutzerdefinierte Rolle',
    domain: 'Domain',
    help_entry: 'Hilfetext',
    integration: 'Integration',
    invitation: 'Einladung',
    market: 'Markt',
    membership: 'Benutzer',
    organization: 'Unternehmen',
    settings: 'Einstellungen',
    setup_step: 'Einrichtungsschritt',
    store: 'Shop',
    webhook_delivery: 'Webhook-Zustellung',
    webhook_endpoint: 'Webhook',
  },
  healthClaims: {
    treatment: 'Behandlungsaussage',
    cure: 'Heilungsaussage',
    prevention: 'Vorbeugungsaussage',
    disease: 'Krankheitsbezeichnung',
    drug: 'Arzneimittelbezogene Formulierung',
    guarantee: 'Garantiertes Ergebnis',
  },
  publishBlockers: {
    product_not_active: 'Das Produkt ist nicht aktiv.',
    vet_approval_required: 'Gesundheitsbezogene Inhalte erfordern eine tierärztliche Freigabe.',
    base_price_required: 'In der Basispreisliste des Shops ist kein Preis hinterlegt.',
  },
  events: {
    'organization.updated': 'Unternehmensdaten geändert',
    'brand.created': 'Marke erstellt',
    'brand.updated': 'Marke aktualisiert',
    'store.created': 'Shop erstellt',
    'store.updated': 'Shop aktualisiert',
    'member.invited': 'Benutzer eingeladen',
    'member.updated': 'Benutzerrolle/-status geändert',
    'integration.error': 'Integrationsfehler',
    'integration.connected': 'Integration verbunden',
    'webhook.test': 'Testereignis',
    'product.created': 'Produkt erstellt',
    'product.updated': 'Produkt aktualisiert',
    'product.published': 'Produkt veröffentlicht',
    'product.unpublished': 'Veröffentlichung des Produkts aufgehoben',
    'price.changed': 'Preis geändert',
    'stock.changed': 'Bestand geändert',
    'customer.created': 'Kunde erstellt',
    'dealer.applied': 'Händlerbewerbung eingegangen',
    'dealer.approved': 'Händler genehmigt',
    'order.created': 'Bestellung erstellt',
    'order.status_changed': 'Bestellstatus geändert',
    'order.updated': 'Bestellung bearbeitet',
    'order.items_cancelled': 'Artikel einer Bestellung storniert (Teilstornierung)',
    'payment.captured': 'Zahlung eingezogen',
    'payment.failed': 'Zahlung fehlgeschlagen',
    'payment.review_required': 'Zahlung muss geprüft werden (manuelle Entscheidung erforderlich)',
    'store.went_live': 'Shop ist live',
    'return.requested': 'Rücksende-/Widerrufsanfrage eröffnet',
    'privacy.requested': 'Betroffenenanfrage eingegangen (KVKK)',
    'privacy.anonymized': 'Löschantrag: personenbezogene Daten anonymisiert',
    'form.submitted': 'Nachricht über das Kontaktformular eingegangen',
    'dealer.balance_paid': 'Händler hat seinen Kontosaldo per Karte bezahlt',
    'return.completed': 'Rücksendeanfrage abgeschlossen',
    'theme.published': 'Theme veröffentlicht',
    'page.published': 'Seite veröffentlicht',
    'post.published': 'Blogbeitrag veröffentlicht',
    'refund.created': 'Erstattung durchgeführt',
    'shipment.created': 'Sendung erstellt',
    'shipment.updated': 'Sendungsstatus geändert',
    'invoice.sent': 'Rechnung gesendet',
    'legal.published': 'Rechtstext veröffentlicht',
  },
} satisfies PartialMessages;
